import React, { FC, useState } from 'react'
import { Drawer, Flex, Typography, Tabs, Divider } from 'antd'
import { CloseOutlined, BankOutlined, ReadOutlined, TranslationOutlined } from '@ant-design/icons'
import type { STREntityResponse } from '../types'

const { Text } = Typography

type HostEntry = NonNullable<STREntityResponse['overviewDetails']['hosts']>[number]

const PLATFORM_ICONS: Record<string, string> = {
  airbnb: require('components/BusinessSource/assets/original/airbnb.svg'),
  vrbo: require('../assets/vrbo.svg'),
}

const NA = 'N/A'

interface HostProfileDrawerProps {
  open: boolean
  onClose: () => void
  hosts?: HostEntry[]
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 500,
  fontSize: 16,
  color: '#000000D9',
}

const VALUE_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 500,
  fontSize: 14,
  letterSpacing: 0,
}

const formatHostingSince = (since: string | null): string => {
  if (!since) return NA
  const date = new Date(since)
  if (isNaN(date.getTime())) return NA
  return String(date.getFullYear())
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const HostProfileDrawer: FC<HostProfileDrawerProps> = ({ open, onClose, hosts }) => {
  const [activeTab, setActiveTab] = useState('0')

  const hostList = hosts ?? []
  const host = hostList[Number(activeTab)] ?? hostList[0] ?? null

  const tabItems = hostList.map((h, idx) => ({
    key: String(idx),
    label: (
      <Flex align='center' gap={6}>
        {PLATFORM_ICONS[h.platform] && (
          <img src={PLATFORM_ICONS[h.platform]} alt={h.platform} style={{ width: 16, height: 16 }} />
        )}
        {h.name?.trim() || capitalize(h.platform)}
      </Flex>
    ),
  }))

  return (
    <Drawer
      title={<span style={TITLE_STYLE}>Host Profile Info</span>}
      placement='right'
      width={360}
      open={open}
      onClose={onClose}
      mask={false}
      rootStyle={{ top: 45, height: 'calc(100vh - 45px)' }}
      closable={false}
      extra={<CloseOutlined style={{ color: '#00000073', fontSize: 16, cursor: 'pointer' }} onClick={onClose} />}
    >
      <Tabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }} />

      <Flex gap={16} wrap='wrap' style={{ marginBottom: 20 }}>
        <Flex align='center' gap={4}>
          <Text type='secondary' style={{ fontSize: 13 }}>
            Reviews
          </Text>
          <Text style={VALUE_STYLE}>{host?.reviewsCount ?? NA}</Text>
        </Flex>
        <Flex align='center' gap={4}>
          <Text type='secondary' style={{ fontSize: 13 }}>
            Hosting since
          </Text>
          <Text style={VALUE_STYLE}>{formatHostingSince(host?.since ?? null)}</Text>
        </Flex>
        <Flex align='center' gap={4}>
          <Text type='secondary' style={{ fontSize: 13 }}>
            Rating
          </Text>
          <Text style={VALUE_STYLE}>{host?.rating ?? NA}</Text>
        </Flex>
      </Flex>

      <Divider style={{ margin: '12px 0' }} />
      <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
        ABOUT:
      </Text>
      <Text style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>{host?.about || NA}</Text>

      <Divider style={{ margin: '16px 0' }} />

      {/* TODO: Add when we have the data for works at */}
      {/* <Flex align='center' gap={8} style={{ marginBottom: 8 }}>
        <BankOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
        <Text style={{ fontSize: 13 }}>
          <Text type='secondary'>Works at </Text>
          <Text style={VALUE_STYLE}>{NA}</Text>
        </Text>
      </Flex> */}

      <Flex align='center' gap={8} style={{ marginBottom: 8 }}>
        <ReadOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
        <Text style={{ fontSize: 13 }}>
          <Text type='secondary'>My work: </Text>
          <Text style={VALUE_STYLE}>{host?.workAs || NA}</Text>
        </Text>
      </Flex>

      <Flex align='center' gap={8} style={{ marginTop: 8 }}>
        <TranslationOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
        <Text style={{ fontSize: 13 }}>
          <Text type='secondary'>Speaks </Text>
          <Text style={VALUE_STYLE}>{host?.languages?.length ? host.languages.join(', ') : NA}</Text>
        </Text>
      </Flex>
    </Drawer>
  )
}

export default HostProfileDrawer
